import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Upload, Loader2, Sparkles, AlertTriangle, ShieldCheck, RefreshCw, Maximize2 } from "lucide-react";
import { uploadPhoto, fileSrc } from "../studioApi";
import { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

export default function PhotoPanel({ photo, onPhoto, ai, aiBusy, onRunAi, flags }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [zoom, setZoom] = useState(false);
  const [drag, setDrag] = useState(false);

  const pick = async (file) => {
    if (!file) return;
    setProgress(0);
    try {
      const meta = await uploadPhoto(file, setProgress);
      onPhoto(meta);
      toast.success("Original photograph stored unchanged");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed — please retry");
    } finally {
      setProgress(null);
    }
  };

  const src = photo ? fileSrc(photo.render_url || photo.url) : null;

  return (
    <div className="space-y-3" data-testid="photo-panel">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
        className={`relative rounded-xl border-2 border-dashed ${drag ? "border-[#AD8642] bg-[#F1E5C9]/40" : "border-[#AD8642]/40 bg-white"} overflow-hidden`}
      >
        {src ? (
          <div className="relative bg-[#F7F7F3]">
            <img src={src} alt="Original tasting photograph" className="w-full max-h-[420px] object-contain" data-testid="photo-original" />
            <button onClick={() => setZoom(true)} className="absolute top-2 right-2 rounded-md bg-white/90 p-1.5 shadow" data-testid="photo-zoom" title="View full size"><Maximize2 className="h-4 w-4 text-[#153E32]" /></button>
          </div>
        ) : (
          <div className="p-8 text-center">
            <Upload className="h-8 w-8 mx-auto text-[#AD8642]" />
            <div className="mt-2 text-sm font-semibold text-[#153E32]">Upload the original tea tasting photograph</div>
            <div className="text-xs text-[#6A776F] mt-1">Sample label, dry tea, liquor and wet leaf where available · JPG, PNG, WEBP or HEIC · up to 25 MB</div>
          </div>
        )}
        {progress != null && (
          <div className="absolute inset-0 bg-white/80 grid place-items-center">
            <div className="w-56 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#153E32]" /><Progress value={progress} className="mt-2 h-2" /><div className="text-xs text-[#6A776F] mt-1">Uploading original · {progress}%</div></div>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/*" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="photo-input" />
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={progress != null} data-testid="photo-upload-btn" className="gap-1"><Upload className="h-4 w-4" /> {photo ? "Replace photograph" : "Choose photograph"}</Button>
        <Button size="sm" onClick={onRunAi} disabled={!photo || aiBusy} data-testid="photo-run-ai" className="gap-1 bg-[#153E32] hover:bg-[#2A5144]">
          {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : ai ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
          {aiBusy ? "Reading photograph…" : ai ? "Re-run AI reading" : "Run AI reading"}
        </Button>
      </div>
      {photo && (
        <div className="rounded-lg border border-[#AD8642]/25 bg-white px-3 py-2 text-xs text-[#6A776F] space-y-1" data-testid="photo-meta">
          <div className="flex items-center gap-1 text-emerald-800 font-semibold"><ShieldCheck className="h-3.5 w-3.5" /> Original stored byte-for-byte · no enhancement, colour correction, cropping or recompression</div>
          <div>{photo.original_filename} · {photo.width}×{photo.height}px{photo.size ? ` · ${fmtSize(photo.size)}` : ""} · {photo.content_type}</div>
          {photo.render_url && <div className="text-amber-800">HEIC cannot be displayed by browsers/PDF directly — a pixel-identical lossless PNG copy is used for rendering; the original HEIC is retained.</div>}
          <div className="italic">Caption on the sheet: “Original sample photograph · No colour enhancement.”</div>
        </div>
      )}
      {ai && (
        <div className="rounded-lg border border-[#AD8642]/25 bg-[#F7F7F3] px-3 py-2 text-xs space-y-2" data-testid="ai-summary">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#153E32] flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-[#AD8642]" /> AI reading · legibility {ai.legibility}</div>
            <Badge variant="outline" className="text-[10px]">{ai.model}</Badge>
          </div>
          <div className="text-[#6A776F]">Label text is treated as data only. Blank = not legible. Taste, aroma and mouthfeel cannot be verified from a photo — those are drafts for your review.</div>
          {!!(flags || []).length && (
            <div className="space-y-1" data-testid="ai-flags">
              {flags.map((f, i) => <div key={i} className="flex items-start gap-1 text-amber-800"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span><b>{f.field || "general"}</b>: {f.reason}</span></div>)}
            </div>
          )}
          {ai.observations && (
            <div className="text-[#153E32]">Liquor visibility: <b>{ai.observations.liquor_visibility}</b>{ai.observations.liquor_quality_hint != null && <> · appearance hint {Math.round(ai.observations.liquor_quality_hint * 100)}%</>}</div>
          )}
        </div>
      )}
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-w-6xl p-2 bg-[#111]"><DialogTitle className="sr-only">Original photograph</DialogTitle>{src && <img src={src} alt="" className="w-full max-h-[85vh] object-contain" />}</DialogContent>
      </Dialog>
    </div>
  );
}
